import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";

const PAGES = [
  { key: "home", label: "Home" },
  { key: "about", label: "About" },
  { key: "services", label: "Services" },
  { key: "portfolio", label: "Portfolio" },
  { key: "pricing", label: "Pricing" },
  { key: "blog", label: "Blog" },
  { key: "contact", label: "Contact" },
];

export default function SeoSettings() {
  const [active, setActive] = useState("home");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/data/seo").then((r) => {
      const map = {};
      r.data.forEach((d) => { map[d.page] = d; });
      setData(map);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const current = data[active] || {};
  const setField = (k, v) => setData((d) => ({ ...d, [active]: { ...(d[active] || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/data/seo/${active}`, {
        title: current.title || "",
        description: current.description || "",
        keywords: current.keywords || "",
        og_image: current.og_image || "",
      });
      toast.success("SEO settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-vantage" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-seo">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-white">SEO Settings</h1>
        <p className="mt-1 text-sm text-slate-400">Manage meta titles, descriptions and social tags per page.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setActive(p.key)}
            data-testid={`seo-tab-${p.key}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              active === p.key ? "bg-vantage text-white" : "border border-white/10 text-slate-400 hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="max-w-3xl space-y-5 rounded-2xl border border-white/10 bg-ink-900 p-8">
        <div>
          <Label className="text-slate-300">Meta Title</Label>
          <Input value={current.title || ""} onChange={(e) => setField("title", e.target.value)} className="mt-2 border-white/10 bg-white/[0.03]" data-testid="seo-title" placeholder="Page title for search engines" />
          <p className="mt-1 text-xs text-slate-500">{(current.title || "").length}/60 characters</p>
        </div>
        <div>
          <Label className="text-slate-300">Meta Description</Label>
          <Textarea value={current.description || ""} onChange={(e) => setField("description", e.target.value)} rows={3} className="mt-2 border-white/10 bg-white/[0.03]" data-testid="seo-description" placeholder="Description shown in search results" />
          <p className="mt-1 text-xs text-slate-500">{(current.description || "").length}/160 characters</p>
        </div>
        <div>
          <Label className="text-slate-300">Keywords</Label>
          <Input value={current.keywords || ""} onChange={(e) => setField("keywords", e.target.value)} className="mt-2 border-white/10 bg-white/[0.03]" data-testid="seo-keywords" placeholder="comma, separated, keywords" />
        </div>
        <div>
          <Label className="text-slate-300">Open Graph Image URL</Label>
          <Input value={current.og_image || ""} onChange={(e) => setField("og_image", e.target.value)} className="mt-2 border-white/10 bg-white/[0.03]" data-testid="seo-ogimage" placeholder="https://..." />
        </div>
        <Button onClick={save} disabled={saving} className="bg-vantage hover:bg-vantage-hover" data-testid="seo-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save changes</>}
        </Button>
      </div>
    </div>
  );
}
